import { useEffect, useRef, useState } from 'react';
import { posService } from '@/services/pos.service';
import type { FulfillmentMode, OrderItem, PosVariantDetailItem } from '../types';

export interface StalePricePreview {
  price: number;
  appliedBid?: number;
  appliedAsk?: number;
  appliedTs?: string;
  appliedOverride?: boolean;
  instrumentCode?: string;
}

interface Opts {
  orderItems: OrderItem[];
  staleItemIds: Set<string>;
  fulfillmentMode: FulfillmentMode;
}

function matchStaleItem(
  detail: PosVariantDetailItem,
  staleItems: OrderItem[],
): OrderItem | undefined {
  const serial = detail.stock_serial?.serial;
  const vcode = detail.variant.variant_code;
  return staleItems.find((s) => {
    if (serial && s.specificationCode === serial) return true;
    if (!serial && !s.specificationCode && s.variantCode === vcode) return true;
    return false;
  });
}

/**
 * Auto-fetches preview prices from POST /pos/variants/lookup whenever the
 * set of stale items (or the quote behind them) changes. The returned map is
 * the source of truth for "old → new" inline rendering in the order table.
 *
 * Decoupled from useRefreshStalePrices so the apply-on-confirm path stays
 * synchronous in unit tests (the hook there accepts a preview map as input).
 */
export function useStalePricePreview(opts: Opts): Map<string, StalePricePreview> {
  const { orderItems, staleItemIds, fulfillmentMode } = opts;
  const [preview, setPreview] = useState<Map<string, StalePricePreview>>(new Map());
  const lastFetchKeyRef = useRef<string>('');

  useEffect(() => {
    if (staleItemIds.size === 0) {
      lastFetchKeyRef.current = '';
      setPreview(new Map());
      return;
    }

    const staleItems = orderItems.filter((it) => staleItemIds.has(it.id));
    if (staleItems.length === 0) return;

    // Key by ids + applied bid/ask/override so a NEW WS push (different snapshot)
    // for the same id re-triggers the fetch. Without this, second push silently
    // reuses stale preview.
    const fetchKey = staleItems
      .map((it) => `${it.id}@${it.appliedBid ?? ''}/${it.appliedAsk ?? ''}/${it.appliedOverride ?? ''}`)
      .sort()
      .join('|');
    if (fetchKey === lastFetchKeyRef.current) return;
    lastFetchKeyRef.current = fetchKey;

    const qrCodes = staleItems.map((it) => it.specificationCode ?? it.variantCode);
    let cancelled = false;
    posService
      .lookupVariants(qrCodes, fulfillmentMode)
      .then((res) => {
        if (cancelled) return;
        const next = new Map<string, StalePricePreview>();
        for (const detail of res.items) {
          const matched = matchStaleItem(detail, staleItems);
          if (!matched) continue;
          const newPrice = detail.price.applied_price ?? detail.price.price;
          next.set(matched.id, {
            price: newPrice,
            appliedBid: detail.price.applied_bid,
            appliedAsk: detail.price.applied_ask,
            appliedTs: detail.price.applied_ts,
            appliedOverride: detail.price.override_applied,
            instrumentCode: detail.price.instrument_code,
          });
        }
        setPreview(next);
      })
      .catch(() => {
        // Preview is best-effort. Silent fail keeps the banner usable as a
        // fallback path — the user clicking "Cập nhật" will retry the fetch.
      });

    return () => {
      cancelled = true;
    };
  }, [orderItems, staleItemIds, fulfillmentMode]);

  return preview;
}
